import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, Aperture, Plane, Layers, LayoutGrid, MapPin, Sparkles, Receipt, Puzzle, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import PricingCard from "../components/PricingCard";
import PricingHints from "../components/PricingHints";
import useReveal from "../hooks/useReveal";

const PACKAGES = [
  {
    name: "Premium",
    price: 597,
    tagline: "Für besondere Objekte.",
    description:
      "Für Immobilien mit höherem Präsentationsanspruch, größerem Umfang oder besonderer Lage.",
    features: [
      "20 professionell bearbeitete Immobilienfotos",
      "6 Detailaufnahmen für Atmosphäre, Ausstattung und Wertigkeit",
      "4 Drohnenbilder für eine stärkere Gesamtwirkung",
      "Erweiterte Bildstrecke für Exposé, Portale und Social Media",
      "Hochwertige, natürliche Nachbearbeitung",
      "Digitale Lieferung in strukturierter Form",
    ],
  },
  {
    name: "Plus",
    price: 397,
    featured: true,
    badge: "Kundenfavorit",
    tagline: "Empfohlen für die meisten Vermarktungen.",
    description:
      "Für Immobilien, die mit mehr Wirkung, mehr Perspektiven und einem stärkeren ersten Eindruck vermarktet werden sollen.",
    features: [
      "15 professionell bearbeitete Immobilienfotos",
      "4 Detailaufnahmen für Atmosphäre, Ausstattung und Raumgefühl",
      "2 Drohnenbilder für eine hochwertigere Objektpräsentation",
      "Optimiert für Exposé, Immobilienportale und Social Media",
      "Hochwertige, natürliche Nachbearbeitung",
      "Digitale Lieferung in strukturierter Form",
    ],
  },
  {
    name: "Basis",
    price: 297,
    tagline: "Klare Grundpräsentation.",
    description:
      "Professionelle Immobilienfotografie für eine klare und hochwertige Grundpräsentation.",
    features: [
      "10 professionell bearbeitete Immobilienfotos",
      "2 Detailaufnahmen",
      "Natürliche Nachbearbeitung",
      "Optimiert für Exposé und Immobilienportale",
      "Digitale Lieferung in strukturierter Form",
    ],
  },
];

const EXTRAS = [
  {
    icon: Camera,
    title: "Einzelaufnahmen",
    subtitle: "Zusätzliche Räume, Perspektiven oder Details.",
    items: [
      { label: "1 Einzelbild inkl. professioneller Nachbearbeitung", price: "27 €" },
      { label: "5 Einzelbilder inkl. professioneller Nachbearbeitung", price: "120 €" },
    ],
  },
  {
    icon: Aperture,
    title: "Detailaufnahmen",
    subtitle:
      "Für möblierte Wohnräume, hochwertige Materialien, Design, Ausstattung und Atmosphäre — für die stilvolle Hervorhebung von Qualität und Raumgefühl.",
    items: [
      { label: "1 Detailbild inkl. professioneller Nachbearbeitung", price: "27 €" },
      { label: "3 Detailbilder inkl. professioneller Nachbearbeitung", price: "70 €" },
    ],
  },
  {
    icon: Plane,
    title: "Luftaufnahmen",
    subtitle:
      "Für eine stärkere Gesamtwirkung von Immobilie, Grundstück und Lage. Ideal für Häuser, Grundstücke und besondere Lagen.",
    items: [
      { label: "1 Luftbild inkl. professioneller Nachbearbeitung", price: "37 €" },
      { label: "5 Luftbilder inkl. professioneller Nachbearbeitung", price: "150 €" },
    ],
  },
  {
    icon: Layers,
    title: "Virtuelles Staging",
    subtitle:
      "Professionelle 3D-Visualisierung, realistische Möblierung, virtuelle Renovierung oder digitale Entrümpelung — für Wohn- und Gewerbeimmobilien.",
    groups: [
      {
        heading: "Wohnimmobilien (leer)",
        note: "Möblierung eines leerstehenden Wohnraums.",
        items: [
          { label: "1× virtuelles Staging", price: "47 €" },
          { label: "3× virtuelles Staging", price: "120 €" },
        ],
      },
      {
        heading: "Wohnimmobilien (möbliert)",
        note: "Virtuelle Renovierung oder Entrümpelung – Austausch oder Entfernung vorhandener Möbel.",
        items: [
          { label: "1× virtuelles Staging", price: "77 €" },
          { label: "3× virtuelles Staging", price: "190 €" },
        ],
      },
      {
        heading: "Gewerbeimmobilien (leer)",
        note: "Möblierung eines leerstehenden Geschäftsraums.",
        items: [
          { label: "1× virtuelles Staging", price: "87 €" },
          { label: "3× virtuelles Staging", price: "210 €" },
        ],
      },
      {
        heading: "Gewerbeimmobilien (möbliert)",
        note: "Umgestaltung für spezifische Nutzung (Büro, Gastronomie, Handel).",
        items: [
          { label: "1× virtuelles Staging", price: "117 €" },
          { label: "3× virtuelles Staging", price: "280 €" },
        ],
      },
    ],
    wide: true,
  },
  {
    icon: LayoutGrid,
    title: "Grundrissvisualisierung",
    subtitle: "Klare und verständliche Darstellung der Raumstruktur.",
    items: [
      { label: "2D-Grundriss pro Etage", price: "37 €" },
      { label: "3D-Grundriss pro Etage", price: "47 €" },
    ],
  },
];

export default function PricingPhoto() {
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <main data-testid="pricing-photo-page" className="bg-[#F8F5EF]">
      <Seo
        title="Immobilienfotografie Preise"
        description="Transparente Preispakete für professionelle Immobilienfotografie in NRW – Aachen, Köln, Düren. Basis, Plus und Premium ab 297 € zzgl. MwSt."
        path="/leistungen-preise/immobilienfotografie-preise"
      />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-10 md:pb-14 border-b border-[#EAE6DE]">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end reveal">
            <div className="lg:col-span-8">
              <div className="overline mb-5">Leistungen & Preise</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-[76px] leading-[1.02] text-[#1A1A1A] tracking-tight font-light">
                Immobilien&shy;fotografie <br />
                <span className="italic gold-text">Preise</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-[15px] md:text-[17px] leading-[1.6] text-[#3A3A3A] font-light">
                Professionelle Immobilienfotos für einen starken ersten Eindruck — für Exposés,
                Immobilienportale, Websites und Social Media.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <Link
                to="/#kontakt"
                data-testid="pricing-photo-hero-cta"
                className="group inline-flex items-center justify-between px-8 py-4 bg-[#1A1A1A] text-[#F8F5EF] text-xs uppercase tracking-[0.2em] hover:bg-[#FFA500] hover:text-[#1A1A1A] transition-colors duration-300 min-w-[280px] font-medium"
              >
                Jetzt Angebot anfordern
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="pt-16 md:pt-24 pb-24 md:pb-32">
        <div className="container-x">
          <div className="mb-12 md:mb-16 reveal">
            <div className="overline mb-4">Fotopakete</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A] tracking-tight">
              Ein <span className="italic font-light">passendes Paket</span> für <br />
              jede Immobilie.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {PACKAGES.map((p) => (
              <PricingCard key={p.name} {...p} />
            ))}
          </div>

          <div className="mt-12 md:mt-16 flex justify-center reveal">
            <Link
              to="/#kontakt"
              data-testid="pricing-photo-packages-cta"
              className="group inline-flex items-center justify-between px-10 py-5 bg-[#1A1A1A] text-[#F8F5EF] text-xs uppercase tracking-[0.2em] hover:bg-[#FFA500] hover:text-[#1A1A1A] transition-colors duration-300 min-w-[320px] font-medium"
            >
              Jetzt Angebot anfordern
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </div>

          <div className="mt-14 md:mt-16 reveal max-w-3xl">
            <div className="flex items-baseline gap-4 text-[13px] text-[#5A5A5A] font-light border-l-2 border-[#FFA500] pl-6">
              <span className="uppercase tracking-[0.2em] text-[#1A1A1A] text-[11px] font-medium">Lieferzeit</span>
              <span>24 – 48 Stunden</span>
            </div>
          </div>
        </div>
      </section>

      {/* Optional Extensions */}
      <section className="py-24 md:py-32 bg-[#F5F2EC]/50 border-t border-[#EAE6DE]">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 reveal">
            <div className="md:col-span-7">
              <div className="overline mb-4">Optionale Erweiterungen</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1A1A1A] tracking-tight">
                Ergänzen Sie Ihr <br />
                <span className="italic gold-text">Fotopaket</span>.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 md:pt-8">
              <p className="text-[14px] leading-relaxed text-[#5A5A5A] font-light">
                <strong className="text-[#1A1A1A] font-medium">Hinweis:</strong> Nur in Kombination mit
                einem Fotopaket buchbar.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#EAE6DE] border border-[#EAE6DE]">
            {EXTRAS.map((e, idx) => (
              <div
                key={e.title}
                data-testid={`pricing-extra-${idx}`}
                className={`bg-[#F8F5EF] p-8 md:p-10 hover:bg-white transition-colors duration-500 reveal ${
                  e.wide ? "md:col-span-2" : ""
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FFA500] shrink-0">
                    <e.icon size={20} className="text-[#1A1A1A]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-[22px] md:text-[26px] leading-tight text-[#1A1A1A] tracking-tight">
                      {e.title}
                    </h3>
                    <p className="mt-3 text-[13px] leading-relaxed text-[#5A5A5A] font-light">
                      {e.subtitle}
                    </p>
                  </div>
                </div>

                {/* Simple item list */}
                {e.items && (
                  <ul className="mt-6 space-y-3 border-t border-[#EAE6DE] pt-6">
                    {e.items.map((it) => (
                      <li key={it.label} className="flex items-baseline justify-between gap-6 text-[14px]">
                        <span className="text-[#1A1A1A] font-light">{it.label}</span>
                        <span className="ticker-num text-[#FFA500] text-xl whitespace-nowrap">{it.price}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Grouped list (Virtual Staging) */}
                {e.groups && (
                  <div className="mt-6 border-t border-[#EAE6DE] pt-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                    {e.groups.map((g) => (
                      <div key={g.heading}>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-[#1A1A1A] font-medium">
                          {g.heading}
                        </div>
                        {g.note && (
                          <p className="mt-2 text-[12px] leading-relaxed text-[#5A5A5A] font-light">
                            {g.note}
                          </p>
                        )}
                        <ul className="mt-3 space-y-2">
                          {g.items.map((it) => (
                            <li key={it.label} className="flex items-baseline justify-between gap-4 text-[14px]">
                              <span className="text-[#1A1A1A] font-light">{it.label}</span>
                              <span className="ticker-num text-[#FFA500] text-lg whitespace-nowrap">{it.price}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="mt-12 text-[13px] text-[#5A5A5A] font-light">** Alle Preise zzgl. MwSt.</p>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden bg-[#1A1A1A] text-[#F8F5EF]">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8 reveal">
            <div className="overline text-[#FFA500] mb-6">Bereit?</div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Sie möchten Ihre Immobilie <br />
              <span className="gold-text italic">hochwertig präsentieren</span>?
            </h2>
            <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-[#A8A59E] font-light">
              Fordern Sie jetzt ein individuelles Angebot für Ihre Immobilienfotografie an.
              Antwort innerhalb eines Werktages.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end reveal">
            <Link
              to="/#kontakt"
              data-testid="pricing-photo-final-cta"
              className="group inline-flex items-center justify-between px-8 py-4 bg-[#FFA500] text-[#1A1A1A] text-xs uppercase tracking-[0.2em] hover:bg-[#F8F5EF] transition-colors duration-300 min-w-[300px] font-medium"
            >
              Jetzt Angebot anfordern
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
